import { useState, useEffect } from "react";
import { formatRelativeDate } from "../../utils/dateFormat";
import { getMimeIcon } from "../../utils/mimeIcons";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";

interface TrashRowProps {
  name: string;
  kind: "file" | "folder";
  deletedAt: string;
  isAtRoot: boolean;
  /** File-only: used to pick an icon and, for image/video, show a thumbnail */
  mimeType?: string;
  extension?: string;
  thumbnailUrl?: string | null | undefined;
  /** File-only: opens the preview modal */
  onOpen?: () => void;
  onNavigate?: () => void;
  onRestore?: () => void;
  onDelete: () => void;
  isRestoring?: boolean;
  isDeleting?: boolean;
}

const ICON_COLORS: Record<string, string> = {
  image: "text-pink-400",
  video: "text-purple-400",
  audio: "text-indigo-400",
  pdf: "text-red-400",
  code: "text-green-400",
  archive: "text-yellow-400",
  document: "text-blue-400",
  spreadsheet: "text-emerald-400",
  presentation: "text-orange-400",
  generic: "text-gray-400",
};

const RestoreIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 110 14H4a1 1 0 110-2h7a5 5 0 000-10H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const DeleteIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

export function TrashRow({
  name,
  kind,
  deletedAt,
  mimeType,
  extension,
  thumbnailUrl,
  onOpen,
  onNavigate,
  onRestore,
  onDelete,
  isRestoring = false,
  isDeleting = false,
}: TrashRowProps) {
  const dateStr = deletedAt ? formatRelativeDate(deletedAt) : "—";
  const isBusy = isRestoring || isDeleting;

  const iconType = kind === "file" ? getMimeIcon(mimeType ?? "", extension ?? "") : null;
  const [thumbnailError, setThumbnailError] = useState(false);
  useEffect(() => { setThumbnailError(false); }, [thumbnailUrl]);

  return (
    <div className={`flex items-center gap-3 border-b border-gray-800/50 px-4 py-3 text-sm transition-opacity ${isBusy ? "opacity-60" : ""}`}>
      {/* Icon / thumbnail */}
      {kind === "folder" ? (
        <button
          onClick={onNavigate}
          disabled={isBusy}
          className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity disabled:pointer-events-none"
          title="Browse folder contents"
        >
          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
        </button>
      ) : thumbnailUrl && !thumbnailError ? (
        <img
          src={thumbnailUrl}
          alt={name}
          loading="lazy"
          decoding="async"
          className="h-8 w-8 flex-shrink-0 rounded object-cover"
          onError={() => setThumbnailError(true)}
        />
      ) : (
        <svg className={`h-5 w-5 flex-shrink-0 ${ICON_COLORS[iconType ?? "generic"] ?? "text-gray-400"}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      )}

      {/* Name + mobile date */}
      <div className="min-w-0 flex-1">
        {kind === "folder" ? (
          <button
            onClick={onNavigate}
            disabled={isBusy}
            className="block w-full truncate text-left text-gray-400 transition-colors hover:text-gray-200 disabled:pointer-events-none"
          >
            {name}
          </button>
        ) : onOpen ? (
          <button
            onClick={onOpen}
            disabled={isBusy}
            className="block w-full truncate text-left text-gray-400 transition-colors hover:text-gray-200 disabled:pointer-events-none"
          >
            {name}
          </button>
        ) : (
          <span className="block truncate text-gray-400">{name}</span>
        )}
        <div className="mt-0.5 text-xs text-gray-600 sm:hidden">{dateStr}</div>
      </div>

      {/* Deleted date — desktop only */}
      <div className="hidden w-36 flex-shrink-0 text-xs text-gray-500 sm:block">{dateStr}</div>

      {/* Actions */}
      <div className="flex flex-shrink-0 items-center gap-1">
        {isBusy ? (
          /* Show a single centered spinner while action is in-flight */
          <div className="flex h-8 w-8 items-center justify-center sm:w-[138px]">
            <Spinner size="sm" />
          </div>
        ) : (
          <>
            {/* Mobile: icon-only buttons */}
            {onRestore && (
              <button
                onClick={onRestore}
                title="Restore"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-800 hover:text-green-400 sm:hidden"
              >
                <RestoreIcon />
              </button>
            )}
            <button
              onClick={onDelete}
              title="Delete permanently"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-800 hover:text-red-400 sm:hidden"
            >
              <DeleteIcon />
            </button>

            {/* Desktop: text buttons */}
            {onRestore ? (
              <Button variant="ghost" size="sm" onClick={onRestore} className="hidden sm:inline-flex">
                Restore
              </Button>
            ) : (
              <span className="hidden sm:inline-block sm:w-[62px]" />
            )}
            <Button variant="danger" size="sm" onClick={onDelete} className="hidden sm:inline-flex">
              Delete
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
